import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Settings2, Download, FilePlus2, Wallet, AlertTriangle, CheckCircle2, FileClock, X, BellRing, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, downloadAuthed } from "@/lib/api";
import { fmtDate, fmtMoney } from "@/lib/format";
import { DepotTag } from "@/components/DepotPicker";
import { InvoiceStatusBadge } from "@/components/invoices/InvoiceStatusBadge";
import { InvoiceDialog } from "@/components/invoices/InvoiceDialog";
import { InvoiceSettingsDialog } from "@/components/invoices/InvoiceSettingsDialog";
import { PaidAlertsButton } from "@/components/invoices/PaidAlertsButton";
import { ChaseDialog, StatementDialog } from "@/components/invoices/ChaseDialogs";

const FILTERS = [["all", "All"], ["draft", "Drafts"], ["unpaid", "Unpaid"], ["overdue", "Overdue"], ["paid", "Paid"], ["void", "Void"]];
const PICK = { draft: (i) => i.status === "draft", unpaid: (i) => i.status === "sent", overdue: (i) => i.display_status === "overdue", paid: (i) => i.status === "paid", void: (i) => i.status === "void" };

const Stat = ({ icon: Icon, label, value, sub, tone, testId }) => (
  <div className="bg-card border border-border rounded-xl p-4" data-testid={testId}>
    <p className={`text-xs inline-flex items-center gap-1.5 ${tone || "text-muted-foreground"}`}><Icon className="w-3.5 h-3.5" /> {label}</p>
    <p className="font-display text-2xl font-bold mt-1 tabular-nums">{value}</p>
    {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
  </div>
);

const EXPORT_STATUSES = FILTERS.filter(([k]) => k !== "draft");

const ExportDialog = ({ open, onClose }) => {
  const [range, setRange] = useState({ start: "", end: "", status: "all" });
  const go = async () => {
    const q = new URLSearchParams(Object.entries(range).filter(([k, v]) => v && !(k === "status" && v === "all"))).toString();
    const name = `invoices${range.status !== "all" ? `-${range.status}` : ""}.csv`;
    try { await downloadAuthed(`/invoices/export.csv${q ? `?${q}` : ""}`, name); onClose(); }
    catch (e) { toast.error(e.message || "Export failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-display">Export invoices</DialogTitle><DialogDescription>A CSV for your accountant, one row per invoice. Leave the dates blank to export everything (drafts aren't included).</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Issued from</Label><Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="invoice-export-from" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Issued to</Label><Input type="date" value={range.end} min={range.start} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="invoice-export-to" /></div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Status</Label>
          <div className="flex flex-wrap gap-1.5" data-testid="invoice-export-status">
            {EXPORT_STATUSES.map(([k, label]) => (
              <button key={k} type="button" onClick={() => setRange({ ...range, status: k })} aria-pressed={range.status === k}
                className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${range.status === k ? "bg-primary text-white border-primary" : "border-border hover:bg-muted"}`}
                data-testid={`invoice-export-status-${k}`}>{label}</button>
            ))}
          </div>
        </div>
        <DialogFooter><Button onClick={go} className="rounded-full bg-primary hover:bg-primary/90 text-white" data-testid="invoice-export-submit"><Download className="w-4 h-4 mr-2" /> Download CSV</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ReadyToInvoice = ({ jobs, onCreate, busy }) => jobs.length > 0 && (
  <div className="bg-amber-500/5 border border-amber-500/30 rounded-2xl p-4" data-testid="ready-to-invoice">
    <p className="text-sm font-semibold mb-3 inline-flex items-center gap-2"><FileClock className="w-4 h-4 text-amber-600" /> Completed jobs not invoiced yet ({jobs.length})</p>
    <div className="flex flex-wrap gap-2">
      {jobs.slice(0, 12).map((j) => (
        <Button key={j.id} variant="outline" size="sm" disabled={busy === j.id} onClick={() => onCreate(j.id)} className="rounded-full bg-card" data-testid={`ready-invoice-${j.id}`}>
          {busy === j.id ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FilePlus2 className="w-3.5 h-3.5 mr-1.5" />}{j.name}{j.client ? ` · ${j.client}` : ""}
        </Button>
      ))}
    </div>
  </div>
);

const InvoiceTable = ({ rows, onOpen }) => (
  <div className="bg-card border border-border rounded-2xl overflow-x-auto" data-testid="invoices-table">
    <Table>
      <TableHeader><TableRow>
        <TableHead>Invoice</TableHead><TableHead>Client</TableHead><TableHead>Job</TableHead><TableHead>Issued</TableHead><TableHead>Due</TableHead>
        <TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {rows.map((i) => (
          <TableRow key={i.id} onClick={() => onOpen(i.id)} className="cursor-pointer" data-testid={`invoice-row-${i.id}`}>
            <TableCell className="font-semibold whitespace-nowrap">{i.number || <span className="text-muted-foreground font-normal">Draft</span>}</TableCell>
            <TableCell className="min-w-[140px]">{i.client_name || "—"}</TableCell>
            <TableCell className="min-w-[160px]"><div className="flex flex-col items-start gap-1"><span>{i.job_name}</span><DepotTag id={i.depot_id} /></div></TableCell>
            <TableCell className="whitespace-nowrap text-muted-foreground text-sm">{fmtDate(i.issue_date)}</TableCell>
            <TableCell className={`whitespace-nowrap text-sm ${i.display_status === "overdue" ? "text-red-600 font-medium" : "text-muted-foreground"}`}>{fmtDate(i.due_date)}</TableCell>
            <TableCell className="text-right font-semibold tabular-nums whitespace-nowrap" data-testid={`invoice-row-total-${i.id}`}>{fmtMoney(i.total_c)}</TableCell>
            <TableCell><InvoiceStatusBadge status={i.display_status} testId={`invoice-row-status-${i.id}`} /></TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </div>
);

export const InvoicesTab = ({ clientFilter = null, onClearClient }) => {
  const [data, setData] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState(clientFilter ? "unpaid" : "all");
  const [client, setClient] = useState(clientFilter);
  const [openId, setOpenId] = useState(() => new URLSearchParams(window.location.search).get("invoice"));
  const [settingsOpen, setSettingsOpen] = useState(() => new URLSearchParams(window.location.search).get("stripe") === "return");
  const [exportOpen, setExportOpen] = useState(false);
  const [chaseOpen, setChaseOpen] = useState(false);
  const [statementFor, setStatementFor] = useState(null);
  const [creating, setCreating] = useState("");
  const load = async () => {
    try { const [d, j] = await Promise.all([api.invoices(), api.jobs().catch(() => [])]); setData(d); setJobs(j.filter((x) => x.status === "completed")); }
    catch { toast.error("Could not load invoices"); setData({ invoices: [], summary: {} }); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setClient(clientFilter); if (clientFilter) setFilter("unpaid"); }, [clientFilter]);
  const clearClient = () => { setClient(null); onClearClient?.(); };
  const create = async (jobId) => {
    setCreating(jobId);
    try { const inv = await api.createInvoice(jobId); setOpenId(inv.id); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not create invoice"); }
    finally { setCreating(""); }
  };
  if (!data) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;
  const s = data.summary;
  const invoiced = new Set(data.invoices.filter((i) => i.status !== "void").map((i) => i.job_id));
  const byFilter = PICK[filter] ? data.invoices.filter(PICK[filter]) : data.invoices;
  const rows = client ? byFilter.filter((i) => (i.client_name || "").trim().toLowerCase() === client.name.trim().toLowerCase() && (!client.depot_id || i.depot_id === client.depot_id)) : byFilter;
  return (
    <div className="fade-up space-y-6" data-testid="invoices-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h2 className="font-display text-2xl font-bold">Invoices</h2><p className="text-sm text-muted-foreground">Bill clients for completed jobs and track what's owing.</p></div>
        <div className="flex flex-wrap gap-2">
          {s.overdue_count > 0 && <Button onClick={() => setChaseOpen(true)} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="chase-overdue-btn"><BellRing className="w-4 h-4 mr-2" /> Chase all overdue</Button>}
          <PaidAlertsButton />
          <Button variant="outline" onClick={() => setExportOpen(true)} className="rounded-full" data-testid="invoices-export-btn"><Download className="w-4 h-4 mr-2" /> Export CSV</Button>
          <Button variant="outline" onClick={() => setSettingsOpen(true)} className="rounded-full" data-testid="invoices-settings-btn"><Settings2 className="w-4 h-4 mr-2" /> Invoice settings</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Wallet} label="Owing" value={fmtMoney(s.owing_c)} sub={`${s.unpaid_count || 0} unpaid`} testId="invoices-owing" />
        <Stat icon={AlertTriangle} label="Overdue" value={fmtMoney(s.overdue_c)} sub={`${s.overdue_count || 0} invoice${s.overdue_count === 1 ? "" : "s"}`} tone={s.overdue_count ? "text-red-600" : ""} testId="invoices-overdue" />
        <Stat icon={CheckCircle2} label="Paid, last 30 days" value={fmtMoney(s.paid_30d_c)} tone="text-emerald-600" testId="invoices-paid-30d" />
        <Stat icon={FileClock} label="Drafts" value={s.draft_count || 0} sub="not sent yet" testId="invoices-drafts" />
      </div>
      <ReadyToInvoice jobs={jobs.filter((j) => !invoiced.has(j.id))} onCreate={create} busy={creating} />
      <div className="flex flex-wrap gap-2">
        {FILTERS.map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} data-testid={`invoices-filter-${k}`}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-primary text-white" : "bg-secondary text-muted-foreground hover:text-foreground"}`}>{l}</button>
        ))}
        {client && (
          <>
            <button onClick={clearClient} data-testid="invoices-client-chip"
              className="inline-flex items-center gap-1.5 rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-sm font-medium text-primary">
              Client: {client.name} <X className="w-3.5 h-3.5" />
            </button>
            <Button variant="outline" size="sm" onClick={() => setStatementFor(client)} className="rounded-full" data-testid="invoices-client-statement-btn"><Mail className="w-3.5 h-3.5 mr-1.5" /> Email statement</Button>
          </>
        )}
      </div>
      {rows.length === 0
        ? <div className="text-center py-16 border border-dashed border-border rounded-2xl text-muted-foreground" data-testid="invoices-empty">{data.invoices.length ? "No invoices match this filter." : "No invoices yet. Open a completed job and tap Create invoice, or use the list above."}</div>
        : <InvoiceTable rows={rows} onOpen={setOpenId} />}
      <InvoiceDialog invoiceId={openId} onClose={() => setOpenId(null)} onChanged={load} />
      <InvoiceSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <ExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
      <ChaseDialog open={chaseOpen} onClose={() => setChaseOpen(false)} onSent={() => setTimeout(load, 4000)} />
      <StatementDialog client={statementFor} onClose={() => { setStatementFor(null); load(); }} />
    </div>
  );
};
